import { RWaveLoader } from '@rgrd/component-library'
import { Navigate } from 'react-router'

import { useGetCustomersQuery } from '~/shell/api/rtk/generated'
import { useIdentity } from '~/shell/auth/useIdentity'
import { getLastCustomer } from '~/shell/storage/lastCustomer'

import { AppResolutionRedirect } from './AppResolutionRedirect'
import styles from './Bootstrap.module.css'
import { resolveLandingCustomer } from './landingCustomer'

/**
 * Post-auth redirect for a URL with no customer in it: decides which customer the session lands in
 * and sends it there. Renders nothing durable — only a loader while the customer list resolves.
 *
 * Serves both the index route (`/`) and the per-app entry paths (`/arc`, `/scout`). The customer
 * resolved is the same either way; `appId` is only the requested-app input to the shared landing
 * resolver, so an unavailable entry path falls back instead of bypassing permissions.
 *
 * The branches:
 *   1. Customer in the URL → wins, and never reaches here: such a session matches `/c/:customer/`.
 *   2. Single `customer` claim → that customer, with no list to wait on.
 *   3. `regard_admin`, no claim → resolve one from the accessible list (`resolveLandingCustomer`).
 *   4. Neither → unauthorized.
 */
export const Bootstrap = ({ appId }: { appId?: string }) => {
  const { isAdmin, customerClaim } = useIdentity()

  if (customerClaim) {
    return (
      <AppResolutionRedirect customer={customerClaim} requestedAppId={appId} />
    )
  }

  if (!isAdmin) {
    return <Navigate replace to="/unauthorized" />
  }

  return <AdminBootstrap appId={appId} />
}

/**
 * Branch 3: an admin's landing customer comes from the accessible list, so it has to wait for it.
 *
 * A 401 holds the loader rather than falling through to the no-access page: the session is dead and
 * the 401 middleware has already begun a login redirect, so there is nothing to show in between. Any
 * other failure, and an empty list, is terminal.
 */
const AdminBootstrap = ({ appId }: { appId?: string }) => {
  const { data, error, isLoading } = useGetCustomersQuery()

  const isUnauthorized =
    error !== undefined && 'status' in error && error.status === 401
  if (isLoading || isUnauthorized) {
    return (
      <main className={styles.loader}>
        <RWaveLoader label="Loading…" />
      </main>
    )
  }

  if (error !== undefined || !data) {
    return <Navigate replace to="/unauthorized" />
  }

  const customer = resolveLandingCustomer(data.customers, getLastCustomer())

  return customer === undefined ? (
    <Navigate replace to="/unauthorized" />
  ) : (
    <AppResolutionRedirect customer={customer} requestedAppId={appId} />
  )
}
